/**
 * Mic-level alert hysteresis FSM.
 *
 * Watches the live volume reading and decides — with 10s show / 15s hide
 * hysteresis — whether to surface a "clipping", "still-low", or
 * "auto-boosted" mic alert. Owns its own peak-volume and auto-boost-applied
 * tracking refs; reads the current boost state via `boostEnabled` so the
 * "still-low" branch can fire when boost was already on before low audio
 * was detected.
 *
 * Also fires a one-shot 10s startup check that auto-applies boost the very
 * first time monitoring comes online — without this the "auto-boosted"
 * surface only triggers once volume samples cross the low threshold.
 */
import { useEffect, useRef, useState } from "react"
import type { TMicAlert } from "../interfaces"
import { MIC_HIGH_THRESHOLD, MIC_LOW_THRESHOLD } from "../MicLevelMeter/interfaces"

interface IProps {
  volume: number
  monitoring: boolean
  selectedDevice: string | null
  boostEnabled: boolean
  handleBoostToggle: (enabled: boolean) => void
}

interface IUseMicAlertHysteresisResult {
  micAlert: TMicAlert
}

export function useMicAlertHysteresis({
  volume,
  monitoring,
  selectedDevice,
  boostEnabled,
  handleBoostToggle,
}: IProps): IUseMicAlertHysteresisResult {
  const [micAlert, setMicAlert] = useState<TMicAlert>(null)
  const peakVolumeRef = useRef(0)
  const autoBoostAppliedRef = useRef(false)
  const boostEnabledForAlertRef = useRef(boostEnabled)
  boostEnabledForAlertRef.current = boostEnabled

  const micShowTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const micHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const micAlertRef = useRef<TMicAlert>(null)
  micAlertRef.current = micAlert

  // biome-ignore lint/correctness/useExhaustiveDependencies: selectedDevice is an intentional trigger so the check re-runs when the mic changes
  useEffect(() => {
    if (!monitoring) {
      peakVolumeRef.current = 0
      autoBoostAppliedRef.current = false
      if (micShowTimerRef.current) {
        clearTimeout(micShowTimerRef.current)
        micShowTimerRef.current = null
      }
      if (micHideTimerRef.current) {
        clearTimeout(micHideTimerRef.current)
        micHideTimerRef.current = null
      }
      setMicAlert(null)
      return
    }

    if (volume > peakVolumeRef.current) peakVolumeRef.current = volume

    const shouldShow: TMicAlert =
      volume > MIC_HIGH_THRESHOLD
        ? "clipping"
        : volume < MIC_LOW_THRESHOLD
          ? autoBoostAppliedRef.current
            ? boostEnabledForAlertRef.current
              ? "still-low"
              : "auto-boosted"
            : null
          : null

    if (shouldShow !== null && micAlertRef.current !== shouldShow) {
      if (!micShowTimerRef.current) {
        micShowTimerRef.current = setTimeout(() => {
          micShowTimerRef.current = null
          if (micAlertRef.current !== shouldShow) {
            if (shouldShow === "clipping" || shouldShow === "still-low") {
              setMicAlert(shouldShow)
            } else if (shouldShow === "auto-boosted" && !autoBoostAppliedRef.current) {
              autoBoostAppliedRef.current = true
              if (!boostEnabledForAlertRef.current) {
                handleBoostToggle(true)
              }
              setMicAlert("auto-boosted")
            } else {
              setMicAlert(shouldShow)
            }
          }
        }, 10_000)
      }
      if (micHideTimerRef.current) {
        clearTimeout(micHideTimerRef.current)
        micHideTimerRef.current = null
      }
    } else if (shouldShow === null && micAlertRef.current !== null) {
      if (micShowTimerRef.current) {
        clearTimeout(micShowTimerRef.current)
        micShowTimerRef.current = null
      }
      if (!micHideTimerRef.current) {
        micHideTimerRef.current = setTimeout(() => {
          micHideTimerRef.current = null
          if (micAlertRef.current !== null) {
            setMicAlert(null)
            autoBoostAppliedRef.current = false
          }
        }, 15_000)
      }
    } else {
      if (shouldShow !== null && micShowTimerRef.current === null && micHideTimerRef.current) {
        clearTimeout(micHideTimerRef.current)
        micHideTimerRef.current = null
      }
    }
  }, [volume, monitoring, selectedDevice, handleBoostToggle])

  // biome-ignore lint/correctness/useExhaustiveDependencies: selectedDevice intentionally re-triggers on mic change
  useEffect(() => {
    if (!monitoring) return
    const timer = setTimeout(() => {
      if (autoBoostAppliedRef.current) return
      if (peakVolumeRef.current >= MIC_LOW_THRESHOLD) return
      autoBoostAppliedRef.current = true
      if (!boostEnabledForAlertRef.current) {
        handleBoostToggle(true)
        setMicAlert("auto-boosted")
      } else {
        setMicAlert("still-low")
      }
    }, 10_000)
    return () => clearTimeout(timer)
  }, [monitoring, selectedDevice, handleBoostToggle])

  return { micAlert }
}
